import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import {
  ArrowLeft,
  Code,
  Key,
  Server,
  Shield,
  Zap,
  AlertCircle,
  CheckCircle,
  Copy
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../lib/i18n';

interface APIKey {
  id: string;
  key: string;
  name: string | null;
  is_active: boolean;
}

export const APIDocumentation: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [copiedSection, setCopiedSection] = useState<string>('');
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadAPIKeys();
    }
  }, [user]);

  const loadAPIKeys = async () => {
    try {
      setLoadingKeys(true);
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, key, name, is_active')
        .eq('user_id', user?.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const keys = (data || []).filter(key => key !== null);
      setApiKeys(keys);
      if (keys.length > 0) {
        setActiveKey(keys[0].key);
      }
    } catch (error: any) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoadingKeys(false);
    }
  };

  const copyCode = async (code: string, section: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(''), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedSection('api-key');
      setTimeout(() => setCopiedSection(''), 2000);
    } catch (error) {
      console.error('Failed to copy key:', error);
    }
  };

  const CodeBlock: React.FC<{ code: string; section: string; language?: string }> = ({
    code,
    section,
    language = 'javascript'
  }) => (
    <div className="relative group">
      <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-sm overflow-x-auto">
        <code>{code}</code>
      </pre>
      <Button
        onClick={() => copyCode(code, section)}
        variant="outline"
        size="sm"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 hover:bg-gray-700 text-white border-gray-700"
      >
        {copiedSection === section ? (
          <CheckCircle className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Link to="/settings" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('apiDocs.backToSettings')}
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Code className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('apiDocs.title')}</h1>
              <p className="text-gray-600 dark:text-gray-400">{t('apiDocs.subtitle')}</p>
            </div>
          </div>

          {/* API Keys Section */}
          {!loadingKeys && apiKeys.length > 0 && (
            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.yourApiKey')}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.useKeyToAuthenticate')}</p>
                  </div>
                </div>
                <Link to="/settings">
                  <Button variant="outline" size="sm">
                    {t('apiDocs.manageKeys')}
                  </Button>
                </Link>
              </div>
              {activeKey && (
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-3 rounded border border-blue-300 dark:border-blue-700">
                  <code className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                    {activeKey}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyKey(activeKey)}
                  >
                    {copiedSection === 'api-key' ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}
              {apiKeys.length > 1 && (
                <div className="mt-2">
                  <select
                    className="text-sm p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    value={activeKey || ''}
                    onChange={(e) => setActiveKey(e.target.value)}
                  >
                    {apiKeys.map((key) => (
                      <option key={key.id} value={key.key}>
                        {key.name || t('apiDocs.unnamedKey')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {!loadingKeys && apiKeys.length === 0 && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">{t('apiDocs.noApiKeysFound')}</h3>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                    {t('apiDocs.needCreateKey')}
                  </p>
                  <Link to="/settings">
                    <Button size="sm">
                      <Key className="w-4 h-4 mr-2" />
                      {t('apiDocs.createApiKey')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Server className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.restfulApi')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.standardHttpMethods')}</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <Zap className="w-6 h-6 text-green-600 dark:text-green-400 mb-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.fastReliable')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.uptime')}</p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400 mb-2" />
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.secure')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.apiKeyAuth')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Getting Started */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center text-gray-900 dark:text-white">
              <Key className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400" />
              {t('apiDocs.gettingStarted')}
            </h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.baseUrl')}</h3>
                <code className="block p-3 bg-gray-100 dark:bg-gray-900 rounded text-sm text-gray-900 dark:text-gray-100">
                  https://api.dochelper.org/
                </code>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('apiDocs.forLocalDev')} <code>http://localhost:8000</code>
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.authentication')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-3">
                  {t('apiDocs.authMethods')}
                </p>
                <CodeBlock
                  section="auth"
                  code={`Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}`}
                />

                <div className="mt-4 space-y-3">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">{t('apiDocs.method1SupabaseJwt')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('apiDocs.method1Desc')}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">{t('apiDocs.method2CustomKeys')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('apiDocs.method2Desc')} <code className="text-xs">dhk_</code>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-yellow-800 dark:text-yellow-200">{t('apiDocs.securityBestPractices')}</h4>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-1 space-y-1">
                      <li>• {t('apiDocs.securityTip1')}</li>
                      <li>• {t('apiDocs.securityTip2')}</li>
                      <li>• {t('apiDocs.securityTip3')}</li>
                      <li>• {t('apiDocs.securityTip4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Documents API */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{t('apiDocs.documentsApi')}</h2>

            <div className="space-y-8">
              {/* List Documents */}
              <div>
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded text-xs font-mono font-semibold">GET</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/documents</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.retrieveAllDocs')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.queryParameters')}</h4>
                <div className="bg-gray-50 dark:bg-gray-900 rounded p-4 mb-3 space-y-2 text-sm">
                  <div className="flex items-start">
                    <code className="text-blue-600 dark:text-blue-400 mr-2">limit</code>
                    <span className="text-gray-600 dark:text-gray-400">{t('apiDocs.paramLimit')}</span>
                  </div>
                  <div className="flex items-start">
                    <code className="text-blue-600 dark:text-blue-400 mr-2">page</code>
                    <span className="text-gray-600 dark:text-gray-400">{t('apiDocs.paramPage')}</span>
                  </div>
                  <div className="flex items-start">
                    <code className="text-blue-600 dark:text-blue-400 mr-2">sort</code>
                    <span className="text-gray-600 dark:text-gray-400">{t('apiDocs.paramSort')}</span>
                  </div>
                </div>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.exampleRequest')}</h4>
                <CodeBlock
                  section="list-docs"
                  code={`curl -X GET "https://api.dochelper.org/documents?limit=10&page=1&sort=created_desc" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json"`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="list-docs-response"
                  code={`{
  "success": true,
  "data": {
    "documents": [
      {
        "id": "550e8400-e29b-41d4-a716-446655440000",
        "title": "My Document",
        "content": "Document content...",
        "user_id": "user_uuid",
        "metadata": {},
        "created_at": "2025-01-15T10:30:00Z",
        "updated_at": "2025-01-15T14:20:00Z"
      }
    ],
    "total": 42,
    "limit": 10,
    "page": 1
  }
}`}
                />
              </div>

              {/* Get Document */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded text-xs font-mono font-semibold">GET</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/documents/:id</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.retrieveDocById')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.exampleRequest')}</h4>
                <CodeBlock
                  section="get-doc"
                  code={`curl -X GET "https://api.dochelper.org/documents/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json"`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="get-doc-response"
                  code={`{
  "success": true,
  "data": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "title": "My Document",
    "content": "Document content...",
    "user_id": "user_uuid",
    "metadata": {},
    "created_at": "2025-01-15T10:30:00Z",
    "updated_at": "2025-01-15T14:20:00Z"
  }
}`}
                />
              </div>

              {/* Create Document */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs font-mono font-semibold">POST</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/documents</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.createNewDoc')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.requestBody')}</h4>
                <CodeBlock
                  section="create-doc-body"
                  code={`{
  "title": "My New Document",
  "content": "Initial document content...",
  "metadata": {                    // optional
    "tags": ["report", "Q1"],
    "category": "business"
  }
}`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleRequest')}</h4>
                <CodeBlock
                  section="create-doc"
                  code={`curl -X POST "https://api.dochelper.org/documents" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "My New Document",
    "content": "Initial document content...",
    "metadata": {"category": "business"}
  }'`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="create-doc-response"
                  code={`{
  "success": true,
  "data": {
    "id": "660e8400-e29b-41d4-a716-446655440001",
    "title": "My New Document",
    "content": "Initial document content...",
    "user_id": "user_uuid",
    "metadata": {"category": "business"},
    "created_at": "2025-01-15T15:45:00Z",
    "updated_at": "2025-01-15T15:45:00Z"
  }
}`}
                />
              </div>

              {/* Update Document */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 rounded text-xs font-mono font-semibold">PUT</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/documents/:id</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.updateExistingDoc')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.requestBody')}</h4>
                <CodeBlock
                  section="update-doc-body"
                  code={`{
  "title": "Updated Document Title",     // optional
  "content": "Updated content...",       // optional
  "metadata": {                          // optional
    "tags": ["report", "Q2", "final"],
    "status": "final"
  }
}`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleRequest')}</h4>
                <CodeBlock
                  section="update-doc"
                  code={`curl -X PUT "https://api.dochelper.org/documents/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Updated Document Title",
    "content": "Updated content..."
  }'`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="update-doc-response"
                  code={`{
  "success": true,
  "data": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "title": "Updated Document Title",
    "content": "Updated content...",
    "user_id": "user_uuid",
    "metadata": {},
    "created_at": "2025-01-15T10:30:00Z",
    "updated_at": "2025-01-15T16:00:00Z"
  }
}`}
                />
              </div>

              {/* Delete Document */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded text-xs font-mono font-semibold">DELETE</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/documents/:id</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.deleteDocPermanently')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.exampleRequest')}</h4>
                <CodeBlock
                  section="delete-doc"
                  code={`curl -X DELETE "https://api.dochelper.org/documents/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}"`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="delete-doc-response"
                  code={`{
  "success": true,
  "message": "Document deleted successfully"
}`}
                />
              </div>
            </div>
          </section>

          {/* AI Processing API */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{t('apiDocs.aiProcessingApi')}</h2>

            <div className="space-y-8">
              {/* Process Document */}
              <div>
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs font-mono font-semibold">POST</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/ai/process</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.processDocWithAi')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.requestBody')}</h4>
                <CodeBlock
                  section="ai-process-body"
                  code={`{
  "content": "Your document content here...",           // optional if document_id provided
  "document_id": "550e8400-e29b-41d4-a716-446655440000", // optional if content provided
  "operation": "improve|summarize|expand|simplify",     // default: "improve"
  "instructions": "Additional context or requirements"   // optional
}`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleRequest')}</h4>
                <CodeBlock
                  section="ai-process"
                  code={`curl -X POST "https://api.dochelper.org/ai/process" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "This is my document that needs improvement.",
    "operation": "improve",
    "instructions": "Make it more professional and concise"
  }'`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="ai-process-response"
                  code={`{
  "success": true,
  "data": {
    "original_content": "This is my document that needs improvement.",
    "processed_content": "This document has been enhanced for clarity and professionalism...",
    "operation": "improve",
    "tokens_used": 145
  }
}`}
                />
              </div>

              {/* Generate Summary */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs font-mono font-semibold">POST</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/ai/summarize</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.generateSummary')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.requestBody')}</h4>
                <CodeBlock
                  section="ai-summarize-body"
                  code={`{
  "content": "Long document content to summarize...",   // optional if document_id provided
  "document_id": "550e8400-e29b-41d4-a716-446655440000"  // optional if content provided
}`}
                />

                <h4 className="font-semibold mb-2 mt-4 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="ai-summarize-response"
                  code={`{
  "success": true,
  "data": {
    "summary": "Key points from the document summarized concisely...",
    "word_count": 87,
    "tokens_used": 234
  }
}`}
                />
              </div>
            </div>
          </section>

          {/* Templates API */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{t('apiDocs.templatesApi')}</h2>

            <div className="space-y-8">
              {/* List Templates */}
              <div>
                <div className="flex items-baseline space-x-3 mb-3">
                  <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded text-xs font-mono font-semibold">GET</span>
                  <code className="text-lg font-mono text-gray-900 dark:text-gray-100">/templates</code>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-3">{t('apiDocs.retrieveTemplates')}</p>

                <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.exampleResponse')}</h4>
                <CodeBlock
                  section="list-templates-response"
                  code={`{
  "success": true,
  "data": {
    "templates": [
      {
        "id": "aa97a061-2c0f-451f-8785-8517c11a3ebf",
        "name": "Business Report",
        "description": "Professional business report template",
        "category": "business",
        "content": "Template content structure...",
        "created_at": "2025-01-01T00:00:00Z"
      },
      {
        "id": "bb97a061-2c0f-451f-8785-8517c11a3ebf",
        "name": "Cover Letter",
        "description": "Professional cover letter template",
        "category": "career",
        "content": "Template content structure...",
        "created_at": "2025-01-01T00:00:00Z"
      }
    ],
    "total": 24
  }
}`}
                />
              </div>
            </div>
          </section>

          {/* Error Codes */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{t('apiDocs.errorCodes')}</h2>

            <div className="space-y-3">
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <code className="text-red-600 dark:text-red-400 font-semibold">400 Bad Request</code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('apiDocs.err400Label')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.err400Desc')}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <code className="text-red-600 dark:text-red-400 font-semibold">401 Unauthorized</code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('apiDocs.err401Label')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.err401Desc')}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <code className="text-red-600 dark:text-red-400 font-semibold">403 Forbidden</code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('apiDocs.err403Label')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.err403Desc')}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <code className="text-red-600 dark:text-red-400 font-semibold">404 Not Found</code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('apiDocs.err404Label')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.err404Desc')}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <code className="text-red-600 dark:text-red-400 font-semibold">429 Too Many Requests</code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('apiDocs.err429Label')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.err429Desc')}</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <code className="text-red-600 dark:text-red-400 font-semibold">500 Internal Server Error</code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('apiDocs.err500Label')}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.err500Desc')}</p>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.errorResponseFormat')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {t('apiDocs.fastApiFormat')}
              </p>
              <CodeBlock
                section="error-format"
                code={`{
  "detail": "The requested document does not exist"
}`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t('apiDocs.backwardCompatNote1')} <code>"success": false</code> {t('apiDocs.backwardCompatNote2')}
              </p>
            </div>
          </section>

          {/* Rate Limits */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{t('apiDocs.rateLimitsPlanned')}</h2>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>{t('apiDocs.noteLabel')}</strong> {t('apiDocs.rateLimitNote')}
              </p>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('apiDocs.plannedRateLimits')}
            </p>

            <div className="space-y-3">
              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.freePlan')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.limitedApiAccess')}</p>
                  </div>
                  <span className="text-lg font-mono font-semibold text-gray-900 dark:text-gray-100">10 req/min</span>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.proPlan')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.standardApiAccess')}</p>
                  </div>
                  <span className="text-lg font-mono font-semibold text-gray-900 dark:text-gray-100">100 req/min</span>
                </div>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{t('apiDocs.enterprisePlan')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('apiDocs.enhancedApiAccess')}</p>
                  </div>
                  <span className="text-lg font-mono font-semibold text-gray-900 dark:text-gray-100">1000 req/min</span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('apiDocs.rateLimitHeaders')}</h4>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                {t('apiDocs.rateLimitHeadersDesc')}
              </p>
              <CodeBlock
                section="rate-limit-headers"
                code={`X-RateLimit-Limit: 100
X-RateLimit-Remaining: 95
X-RateLimit-Reset: 1642258800`}
              />
            </div>
          </section>

          {/* SDK Examples */}
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">{t('apiDocs.codeExamples')}</h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('apiDocs.javascriptNode')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('apiDocs.jsDesc')}
                </p>
                <CodeBlock
                  section="sdk-js"
                  code={`import { createClient } from '@supabase/supabase-js';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Option 1: Using Supabase JWT token
const { data: authData } = await supabase.auth.signInWithPassword({
  email: 'user@example.com',
  password: 'password'
});
const token = authData.session.access_token;

// Option 2: Using custom API key (recommended for server-side)
const apiKey = '${activeKey || 'YOUR_API_KEY'}';

// Create a document
const response = await fetch('https://api.dochelper.org/documents', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`, // or use token from Option 1
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    title: 'My Document',
    content: 'Initial content...',
    metadata: {}
  })
});

const result = await response.json();
console.log(result.data);`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('apiDocs.python')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('apiDocs.pythonDesc')}
                </p>
                <CodeBlock
                  section="sdk-python"
                  code={`from supabase import create_client
import requests
import os

supabase = create_client(
    os.getenv('SUPABASE_URL'),
    os.getenv('SUPABASE_ANON_KEY')
)

# Option 1: Using Supabase JWT token
auth = supabase.auth.sign_in_with_password({
    'email': 'user@example.com',
    'password': 'password'
})
token = auth.session.access_token

# Option 2: Using custom API key (recommended for server-side)
api_key = '${activeKey || 'YOUR_API_KEY'}'

# Create a document
response = requests.post(
    'https://api.dochelper.org/documents',
    headers={
        'Authorization': f'Bearer {api_key}',  # or use token from Option 1
        'Content-Type': 'application/json'
    },
    json={
        'title': 'My Document',
        'content': 'Initial content...',
        'metadata': {}
    }
)

result = response.json()
print(result['data'])`}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('apiDocs.curl')}</h3>
                <CodeBlock
                  section="sdk-curl"
                  code={`# Create a document
curl -X POST "https://api.dochelper.org/documents" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"My Document","content":"Initial content...","metadata":{}}'

# Get document
curl -X GET "https://api.dochelper.org/documents/550e8400-e29b-41d4-a716-446655440000" \\
  -H "Authorization: Bearer ${activeKey || 'YOUR_API_KEY'}"`}
                />
              </div>
            </div>
          </section>

          {/* Support */}
          <section className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{t('apiDocs.needHelp')}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('apiDocs.needHelpDesc')}
            </p>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400">
              <li>• {t('apiDocs.emailLabel')} support@dochelper.org</li>
              <li>• {t('apiDocs.interactiveApiDocs')} <a href="https://api.dochelper.org/docs" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">https://api.dochelper.org/docs</a></li>
              <li>• {t('apiDocs.alternativeDocs')} <a href="https://api.dochelper.org/redoc" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">https://api.dochelper.org/redoc</a></li>
              <li>• {t('apiDocs.facebook')} <a href="https://www.facebook.com/profile.php?id=61573971372378" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">DocHelper AI</a></li>
              <li>• {t('apiDocs.xTwitter')} <a href="https://x.com/DocHelperAgent" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">@DocHelperAgent</a></li>
            </ul>
          </section>
        </div>

        <div className="text-center mt-8 text-gray-500 dark:text-gray-400 text-sm">
          {t('apiDocs.apiVersion')}
        </div>
      </div>
    </div>
  );
};
